/**
 * Точка входа: собирает карту из манифеста, публикуемого конвейером данных.
 *
 * Порядок важен. Сначала регистрируется протокол pmtiles — до того, как
 * MapLibre попытается запросить хоть один тайл. Затем читается манифест: из
 * него берутся и адреса архивов, и границы, и список слоёв внутри тайлов.
 * Ничего из этого не зашито в код, иначе публикация новых данных требовала бы
 * пересборки фронтенда.
 */

import {
  AttributionControl,
  MapLibreMap,
  NavigationControl,
  ScaleControl,
  setWorkerUrl,
} from "maplibre-gl";
import type { MapGeoJSONFeature } from "maplibre-gl";
import "./style.css";

import {
  BASEMAP_SOURCE_ID,
  ManifestError,
  absoluteTileUrl,
  isRaster,
  loadManifest,
  thematicSources,
  type Manifest,
} from "~/config/manifest";
import { readZoneCode } from "~/config/zones";
import {
  createBasemapStyle,
  rasterSource,
  vectorSource,
  THEMATIC_INSERT_BEFORE,
} from "~/map/basemapStyle";
import { eggAtPoint } from "~/config/easterEggs";
import {
  type AppTheme,
  applyUiTheme,
  normalizeTheme,
  resolveBasemapTheme,
  storeTheme,
  storedTheme,
  watchSystemTheme,
} from "~/ui/theme";
import { crow } from "~/ui/rooster";
import { resolveFonts } from "~/map/fonts";
import { registerPMTilesProtocol } from "~/map/protocol";
import {
  buildThematicGroups,
  groupLayerSpecs,
  type ThematicLayerGroup,
} from "~/map/thematicLayers";
import {
  createPermalinkWriter,
  decodeState,
  type PermalinkState,
  type ViewState,
} from "~/state/permalink";
import { Compare } from "~/map/compare";
import { downloadBlob, exportMapAsPng } from "~/map/exportImage";
import { MeasureTool, type MeasureMode } from "~/map/measure";
import {
  activateOffline,
  downloadArchive,
  isCached,
  offlineSupported,
  removeArchive,
} from "~/offline/archiveCache";
import { SearchIndex } from "~/state/searchIndex";
import { clear, requireElement } from "~/ui/dom";
import { queryParcel } from "~/data/geoportal";
import { ZONE_TABLES_BY_SOURCE } from "~/config/genplanSheetZones";
import type { GenplanZone } from "~/config/genplanZones2024";
import { zoneAtPoint } from "~/map/zoneAtPoint";
import { renderFeatureCard, type SelectedFeature } from "~/ui/featureCard";
import { createHoverHint, firstSentence } from "~/ui/hoverHint";
import { createLayerPanel } from "~/ui/layerPanel";
import { createLegend } from "~/ui/legend";
import { hideOverlay, showError, showLoading, toast } from "~/ui/notice";
import { createSearchBox } from "~/ui/searchBox";
import { createToolsPanel, type ToolsPanelHandles } from "~/ui/toolsPanel";

const DEFAULT_OPACITY = 0.7;
const GLYPHS_URL = `${window.location.origin}/fonts/{fontstack}/{range}.pbf`;

/** Центр Калининграда — используется, только если манифест не дал границ. */
const FALLBACK_VIEW: ViewState = {
  lng: 20.4522,
  lat: 54.7104,
  zoom: 12,
  bearing: 0,
  pitch: 0,
};

interface SelectionRef {
  /** Карта, которой принадлежит объект: в режиме сравнения их две. */
  owner: MapLibreMap;
  sourceId: string;
  sourceLayer: string;
  featureId: string | number;
}

interface AppState {
  theme: AppTheme;
  visible: Set<string>;
  opacities: Map<string, number>;
  /** Положение шторки 0..1; null — режим сравнения выключен. */
  swipe: number | null;
  compareLeft: string | null;
  compareRight: string | null;
  selection: SelectionRef | null;
}

// Падение инициализации обязано быть видимым. Без catch отказ уходит в
// необработанное отклонение промиса: заставка «Загружаю слои карты…» висит
// вечно, и отличить сломанное приложение от медленной сети нельзя ни
// пользователю, ни разработчику.
bootstrap().catch((error: unknown) => {
  console.error("Инициализация карты прервана:", error);
  const overlay = document.querySelector<HTMLDivElement>("#overlay");
  if (overlay) {
    showError(
      overlay,
      "Карта не загрузилась.",
      error instanceof Error ? error.message : "Обновите страницу.",
    );
  }
});
registerServiceWorker();

/**
 * Service worker кэширует оболочку приложения и глифы, но не архивы тайлов —
 * те живут в OPFS и управляются кнопкой офлайн-режима.
 *
 * В режиме разработки не регистрируется: иначе он начинает отдавать
 * закэшированные модули вместо свежих и ломает горячую перезагрузку.
 */
function registerServiceWorker(): void {
  if (import.meta.env.DEV) return;
  if (!("serviceWorker" in navigator)) return;
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch((error: unknown) => {
      console.warn("Service worker не зарегистрирован:", error);
    });
  });
}

async function bootstrap(): Promise<void> {
  const overlay = requireElement<HTMLDivElement>("#overlay");
  const cardNode = requireElement<HTMLElement>("#feature-card");
  const layersPane = requireElement<HTMLDivElement>("#pane-layers");
  const legendPane = requireElement<HTMLDivElement>("#pane-legend");
  const toolsPane = requireElement<HTMLDivElement>("#pane-tools");
  const searchSlot = requireElement<HTMLDivElement>("#search-slot");
  const mapArea = requireElement<HTMLElement>(".map-area");
  const compareContainer = requireElement<HTMLDivElement>("#map-compare");

  wireSidebar();
  showLoading(overlay, "Загружаю слои карты…");

  // Адрес воркера MapLibre задаётся явно. По умолчанию библиотека вычисляет его
  // от import.meta.url, а после сборки Vite это указывает внутрь assets/, где
  // файла воркера нет. Без воркера карта не загружает ни одного тайла и при
  // этом не сообщает об ошибке — см. scripts/copy-maplibre-worker.mjs.
  setWorkerUrl("/vendor/maplibre/maplibre-gl-worker.mjs");

  const protocol = registerPMTilesProtocol();

  let manifest: Manifest;
  try {
    manifest = await loadManifest();
  } catch (error) {
    if (error instanceof ManifestError) {
      showError(overlay, error.message, error.hint);
    } else {
      showError(
        overlay,
        "Карта не загрузилась.",
        "Обновите страницу. Если ошибка повторяется — проверьте, запущен ли сервер тайлов.",
      );
    }
    return;
  }

  const fonts = resolveFonts(manifest.fonts.families);
  if (fonts.regular.length === 0) {
    // Подписи без глифов не отрисуются вообще — предупредить дешевле, чем
    // оставить человека гадать, почему на карте нет ни одного названия.
    console.warn("В /fonts нет ни одного семейства глифов: подписи не появятся.");
  }

  const groups = thematicSources(manifest).flatMap(([sourceId, source], index) =>
    buildThematicGroups({ sourceId, source, index }),
  );

  const restored = decodeState();
  const [restoredLeft, restoredRight] = (restored.compare ?? "").split("|");
  const state: AppState = {
    theme: restored.theme ? normalizeTheme(restored.theme) : (storedTheme() ?? "auto"),
    visible: new Set(restoreVisible(restored.layers, groups)),
    opacities: new Map(groups.map((group) => [group.key, DEFAULT_OPACITY])),
    swipe: restored.swipe ?? null,
    compareLeft: pickGroupKey(groups, restoredLeft, 0),
    compareRight: pickGroupKey(groups, restoredRight, 1),
    selection: null,
  };

  // Оформление применяется до создания карты: иначе страница успевает
  // мелькнуть системной темой. Инлайновый скрипт в <head> сделал бы это ещё
  // раньше, но его запрещает CSP (script-src 'self' без unsafe-inline).
  applyUiTheme(state.theme);

  // Подмена источников на локальные ДО создания карты — обязательна.
  //
  // Источник подложки зашит в стиль, который передаётся в конструктор. Если
  // архивы уже скачаны, но подмена произойдёт позже, MapLibre успеет сходить
  // в сеть за метаданными источника, в офлайне получит ошибку и больше к
  // этому источнику не вернётся. Страница откроется из service worker, а
  // карта останется пустой — ровно в том сценарии, ради которого офлайн-режим
  // и делался.
  const offlineUrls = Object.values(manifest.sources).map((source) => absoluteTileUrl(source));
  for (const url of offlineUrls) {
    await activateOffline(protocol, url);
  }

  const view = initialView(restored.view, manifest);
  const map = new MapLibreMap({
    container: "map",
    style: buildStyle(manifest, fonts, state.theme),
    center: [view.lng, view.lat],
    zoom: view.zoom,
    bearing: view.bearing,
    pitch: view.pitch,
    attributionControl: false,
    maxZoom: 20,
    // preserveDrawingBuffer нужен для экспорта вида в PNG: без него WebGL
    // очищает буфер сразу после отрисовки и холст отдаёт пустое изображение.
    // В MapLibre 6 опция живёт внутри canvasContextAttributes.
    canvasContextAttributes: { preserveDrawingBuffer: true },
  });

  map.addControl(new NavigationControl({ visualizePitch: true }), "top-right");
  map.addControl(new ScaleControl({ maxWidth: 120, unit: "metric" }), "bottom-left");
  map.addControl(
    new AttributionControl({ compact: true, customAttribution: manifest.attribution }),
    "bottom-right",
  );

  let pendingStyleHandler: (() => void) | null = null;
  const writePermalink = createPermalinkWriter();
  const syncPermalink = (): void => {
    const center = map.getCenter();
    const permalink: PermalinkState = {
      view: {
        lng: center.lng,
        lat: center.lat,
        zoom: map.getZoom(),
        bearing: map.getBearing(),
        pitch: map.getPitch(),
      },
      layers: [...state.visible],
      theme: state.theme,
      swipe: state.swipe,
      selection: state.selection
        ? `${state.selection.sourceId}__${state.selection.sourceLayer}__${state.selection.featureId}`
        : null,
      compare:
        state.swipe !== null && state.compareLeft && state.compareRight
          ? `${state.compareLeft}|${state.compareRight}`
          : null,
    };
    writePermalink(permalink);
  };

  const applyThematicLayers = (): void => {
    for (const [sourceId, source] of thematicSources(manifest)) {
      if (map.getSource(sourceId)) continue;
      const build = isRaster(source) ? rasterSource : vectorSource;
      map.addSource(
        sourceId,
        build(
          absoluteTileUrl(source),
          manifest.attribution,
          source.minzoom,
          source.maxzoom,
          source.bounds,
        ),
      );
    }

    const before = map.getLayer(THEMATIC_INSERT_BEFORE) ? THEMATIC_INSERT_BEFORE : undefined;
    for (const group of groups) {
      const opacity = state.opacities.get(group.key) ?? DEFAULT_OPACITY;
      for (const spec of groupLayerSpecs(group, opacity)) {
        if (map.getLayer(spec.id)) continue;
        map.addLayer(spec, before);
      }
      setGroupVisibility(map, group, state.visible.has(group.key));
    }
  };

  /**
   * Пометить объект выбранным или снять пометку.
   *
   * Проверка getSource обязательна: между setStyle и загрузкой нового стиля
   * источников в карте нет, и вызов setFeatureState в этот момент (клик, Escape)
   * выбрасывает исключение прямо в обработчике события.
   */
  const markSelection = (reference: SelectionRef, selected: boolean): void => {
    if (!reference.owner.getSource(reference.sourceId)) return;
    reference.owner.setFeatureState(
      {
        source: reference.sourceId,
        sourceLayer: reference.sourceLayer,
        id: reference.featureId,
      },
      { selected },
    );
  };

  const selectFeature = (feature: MapGeoJSONFeature | null, owner?: MapLibreMap): void => {
    if (state.selection) {
      markSelection(state.selection, false);
      state.selection = null;
    }

    if (!feature) {
      renderFeatureCard(cardNode, null, cardCallbacks);
      syncPermalink();
      return;
    }

    const group = groups.find(
      (candidate) =>
        candidate.sourceId === feature.source && candidate.sourceLayer === feature.sourceLayer,
    );

    if (feature.id !== undefined && feature.sourceLayer) {
      state.selection = {
        owner: owner ?? map,
        sourceId: feature.source,
        sourceLayer: feature.sourceLayer,
        featureId: feature.id,
      };
      markSelection(state.selection, true);
    }

    const selected: SelectedFeature = {
      layerTitle: group?.title ?? feature.sourceLayer ?? "Объект",
      sourceLayer: feature.sourceLayer ?? "",
      properties: feature.properties ?? {},
      geometry: feature.geometry,
    };
    renderFeatureCard(cardNode, selected, cardCallbacks);
    syncPermalink();
  };

  const cardCallbacks = {
    onClose: () => {
      selectFeature(null);
    },
    onCopyLink: () => {
      void copyToClipboard(window.location.href);
    },
  };

  // ---------------------------------------------------------------------
  // Поиск
  // ---------------------------------------------------------------------
  const searchIndex = new SearchIndex();
  searchSlot.appendChild(
    createSearchBox({
      index: searchIndex,
      onPick: (result) => {
        map.flyTo({
          center: [result.c[0], result.c[1]],
          // Кадастровый участок и населённый пункт требуют разного масштаба:
          // на z17 не виден город, на z13 не виден участок.
          zoom: result.k === "place" ? 13 : 17,
          duration: 900,
        });
      },
    }),
  );

  // ---------------------------------------------------------------------
  // Сравнение: вторая карта поверх первой, обрезанная по шторке
  // ---------------------------------------------------------------------
  let compare: Compare | null = null;
  let compareMap: MapLibreMap | null = null;

  const compareActive = (): boolean => state.swipe !== null && compareMap !== null;

  /**
   * В режиме сравнения левая карта показывает ровно один слой, правая — ровно
   * один. Показывать «всё слева и один справа» бессмысленно: непонятно, что с
   * чем сравнивается.
   */
  /**
   * Применяет текущую тему к оформлению и запоминает выбор.
   *
   * Стиль карты здесь не трогается: у него своя, более дорогая процедура со
   * снятием и восстановлением всех слоёв, и вызывать её на каждое обращение
   * незачем.
   */
  /**
   * Убирает подсказку.
   *
   * Нужна при открытии карточки: на сенсорном экране подсказку показывает то же
   * касание, что открывает карточку, и она повисает поверх неё, повторяя тот же
   * заголовок. Счётчик увеличивается, чтобы запоздавший ответ чтения растра не
   * показал подсказку заново поверх уже открытой карточки.
   */
  const hideHint = (): void => {
    window.clearTimeout(hintTimer);
    hintToken += 1;
    hint.hide();
  };

  const applyTheme = (): void => {
    applyUiTheme(state.theme);
    storeTheme(state.theme);
  };

  const applyCompareVisibility = (): void => {
    for (const group of groups) {
      const visible = compareActive()
        ? group.key === state.compareLeft
        : state.visible.has(group.key);
      setGroupVisibility(map, group, visible);
    }
  };

  const disableCompare = (): void => {
    // Объект мог быть выбран на правой карте, которую сейчас уничтожат:
    // карточка осталась бы открытой, а подсветки на живой карте нет.
    if (state.selection && compareMap && state.selection.owner === compareMap) {
      selectFeature(null);
    }
    compare?.destroy();
    compare = null;
    compareMap?.remove();
    compareMap = null;
    compareContainer.hidden = true;
    state.swipe = null;
    applyCompareVisibility();
    syncPermalink();
  };

  const enableCompare = (): void => {
    if (compareMap) return;
    const right = groups.find((group) => group.key === state.compareRight);
    if (!right) {
      toast("Нужно выбрать слой для правой половины");
      return;
    }

    state.swipe ??= 0.5;
    compareContainer.hidden = false;

    compareMap = new MapLibreMap({
      container: compareContainer,
      style: buildStyle(manifest, fonts, state.theme),
      center: map.getCenter(),
      zoom: map.getZoom(),
      bearing: map.getBearing(),
      pitch: map.getPitch(),
      attributionControl: false,
      maxZoom: 20,
      // Вся работа мышью идёт через левую карту: правая лежит поверх неё и
      // пропускает события насквозь (pointer-events: none в стилях).
      interactive: false,
    });

    const rightMap = compareMap;
    rightMap.on("load", () => {
      const source = manifest.sources[right.sourceId];
      if (source && !rightMap.getSource(right.sourceId)) {
        const build = isRaster(source) ? rasterSource : vectorSource;
        rightMap.addSource(
          right.sourceId,
          build(
            absoluteTileUrl(source),
            manifest.attribution,
            source.minzoom,
            source.maxzoom,
            source.bounds,
          ),
        );
      }
      const before = rightMap.getLayer(THEMATIC_INSERT_BEFORE) ? THEMATIC_INSERT_BEFORE : undefined;
      for (const spec of groupLayerSpecs(right, state.opacities.get(right.key) ?? DEFAULT_OPACITY)) {
        if (!rightMap.getLayer(spec.id)) rightMap.addLayer(spec, before);
      }

      compare = new Compare({
        container: mapArea,
        left: map,
        right: rightMap,
        position: state.swipe ?? 0.5,
        onPositionChange: (position) => {
          state.swipe = position;
          syncPermalink();
        },
      });
      applyCompareVisibility();
    });

    syncPermalink();
  };

  // ---------------------------------------------------------------------
  // Измерения
  // ---------------------------------------------------------------------
  let tools: ToolsPanelHandles | null = null;
  const measure = new MeasureTool({
    map,
    onResult: (text) => {
      if (tools) tools.measureReadout.textContent = text ?? "";
    },
  });

  // ---------------------------------------------------------------------
  // Экспорт вида
  // ---------------------------------------------------------------------
  const collectVisibleZoneCodes = (): Set<string> => {
    const codes = new Set<string>();
    const zoningLayers = groups
      .filter((group) => group.isZoning && map.getLayer(group.fillLayerId))
      .map((group) => group.fillLayerId);
    if (zoningLayers.length === 0) return codes;
    for (const feature of map.queryRenderedFeatures({ layers: zoningLayers })) {
      const code = readZoneCode(feature.properties);
      if (code) codes.add(code);
    }
    return codes;
  };

  const exportView = async (): Promise<void> => {
    try {
      const blob = await exportMapAsPng({
        map,
        attribution: manifest.attribution,
        visibleZoneCodes: collectVisibleZoneCodes(),
        title: "Генеральный план Калининграда",
      });
      downloadBlob(blob, `genplan-kaliningrad-z${map.getZoom().toFixed(1)}.png`);
      toast("Изображение сохранено");
    } catch (error) {
      console.error(error);
      toast("Не удалось сохранить изображение");
    }
  };

  // ---------------------------------------------------------------------
  // Офлайн-режим
  // ---------------------------------------------------------------------
  const offlineTargets = (): string[] => offlineUrls;

  const refreshOfflineStatus = async (): Promise<void> => {
    if (!tools) return;
    if (!offlineSupported()) {
      tools.offlineStatus.textContent = "Недоступно в этом браузере";
      return;
    }
    const targets = offlineTargets();
    const cached = await Promise.all(targets.map(isCached));
    const ready = cached.filter(Boolean).length;

    if (ready === targets.length) {
      tools.offlineStatus.textContent = `Сохранено архивов: ${ready} из ${targets.length}`;
      return;
    }

    // Объём называется ДО скачивания, а не показывается счётчиком в процессе.
    // Лист генплана — сотни мегабайт, и узнать об этом на середине загрузки с
    // мобильного интернета человек не должен.
    const pending = Object.values(manifest.sources)
      .filter((_source, index) => !cached[index])
      .reduce((sum, source) => sum + source.sizeBytes, 0);
    const megabytes = Math.round(pending / 1024 / 1024);

    tools.offlineStatus.textContent =
      ready === 0
        ? `Офлайн-копии нет — потребуется ${megabytes} МБ`
        : `Сохранено ${ready} из ${targets.length} — осталось ${megabytes} МБ`;
  };

  const downloadOffline = async (): Promise<void> => {
    if (!tools) return;
    const panel = tools;
    const targets = offlineTargets();
    panel.downloadButton.disabled = true;
    panel.offlineProgress.hidden = false;

    try {
      for (const [index, url] of targets.entries()) {
        await downloadArchive(url, ({ receivedBytes, totalBytes }) => {
          const megabytes = (receivedBytes / 1024 / 1024).toFixed(1);
          if (totalBytes) {
            panel.offlineProgress.value = Math.round((receivedBytes / totalBytes) * 100);
            panel.offlineStatus.textContent =
              `Архив ${index + 1} из ${targets.length}: ${megabytes} МБ ` +
              `из ${(totalBytes / 1024 / 1024).toFixed(1)} МБ`;
          } else {
            panel.offlineProgress.removeAttribute("value");
            panel.offlineStatus.textContent = `Архив ${index + 1} из ${targets.length}: ${megabytes} МБ`;
          }
        });
        await activateOffline(protocol, url);
      }
      toast("Карта доступна офлайн");
    } catch (error) {
      console.error(error);
      toast("Скачивание прервано");
    } finally {
      panel.downloadButton.disabled = false;
      panel.offlineProgress.hidden = true;
      await refreshOfflineStatus();
    }
  };

  const removeOffline = async (): Promise<void> => {
    for (const url of offlineTargets()) {
      await removeArchive(url);
    }
    await refreshOfflineStatus();
    toast("Офлайн-копия удалена. Обновите страницу, чтобы вернуться к сетевой загрузке.");
  };

  map.on("load", () => {
    applyThematicLayers();
    hideOverlay(overlay);
    renderPanels();
    restoreSelection(map, groups, restored.selection ?? null, selectFeature);

    void refreshOfflineStatus();

    if (state.swipe !== null) enableCompare();
  });

  map.on("moveend", syncPermalink);
  map.on("rotateend", syncPermalink);
  map.on("pitchend", syncPermalink);

  /**
   * Какая карта отвечает за точку экрана.
   *
   * В режиме сравнения правая половина — отдельный экземпляр MapLibre, лежащий
   * поверх левого и пропускающий события мыши насквозь. Камеры у карт
   * синхронизированы, поэтому экранная точка у обеих одна и та же, и запрос
   * достаточно адресовать нужной из них.
   */
  const mapAt = (x: number): MapLibreMap => {
    if (!compareActive() || !compareMap || !compare) return map;
    return x > mapArea.clientWidth * compare.getPosition() ? compareMap : map;
  };

  const interactiveLayersOf = (target: MapLibreMap): string[] =>
    groups
      .filter((group) => (compareActive() ? true : state.visible.has(group.key)))
      .flatMap((group) => group.interactiveLayerIds)
      .filter((layerId) => target.getLayer(layerId));

  // Запрос справки об участке уходит на чужой сервер и возвращается не
  // мгновенно. Пока он летит, пользователь успевает кликнуть ещё раз —
  // предыдущий запрос отменяется, иначе ответы могут прийти не в том порядке,
  // в каком их спрашивали, и в карточке окажется участок от позапрошлого клика.
  let parcelRequest: AbortController | null = null;

  /**
   * Листы, по которым вообще допустимо определять зону.
   *
   * Не «все видимые растры», а только те, для которых разобрана легенда. Иначе
   * цвет с чужого листа сопоставлялся бы с таблицей 2024 года: совпало бы
   * запросто — схема раскраски у генпланов общая, — и карта уверенно назвала
   * бы зону по чертежу, привязанному с ошибкой в полторы сотни метров, ничем
   * этого не выдав.
   */
  const rasterSourcesInView = (): {
    title: string;
    tileUrl: string;
    maxzoom: number;
    zones?: GenplanZone[];
  }[] =>
    groups
      .filter((group) => group.raster && state.visible.has(group.key))
      .flatMap((group) => {
        // Опрашиваются все включённые листы, а не только те, у которых есть
        // таблица обозначений. Лист без таблицы не назовёт, что именно под
        // курсором, но ответит на более скромный вопрос: нарисовано тут
        // что-нибудь или это пустое поле чертежа.
        const source = manifest.sources[group.sourceId];
        if (!source) return [];
        const zones = ZONE_TABLES_BY_SOURCE[group.sourceId];
        return [
          {
            title: group.title,
            tileUrl: absoluteTileUrl(source),
            maxzoom: source.maxzoom,
            ...(zones ? { zones } : {}),
          },
        ];
      });

  /**
   * Что известно про точку по всем включённым листам.
   *
   * Опознанное обозначение важнее неопознанного: если хоть один лист назвал
   * зону, показывается она, даже когда лист выше по списку только и может
   * сказать «тут что-то нарисовано».
   */
  const lookupSheets = async (
    lngLat: { lng: number; lat: number },
  ): Promise<
    | { kind: "zone"; zone: GenplanZone; sheet: string }
    | { kind: "unknown"; sheet: string }
    | null
  > => {
    let unknown: { kind: "unknown"; sheet: string } | null = null;

    for (const source of rasterSourcesInView()) {
      const found = await zoneAtPoint({ protocol, ...source, lngLat });
      if (found?.kind === "zone") {
        return { kind: "zone", zone: found.zone, sheet: source.title };
      }
      if (found?.kind === "unknown" && !unknown) {
        unknown = { kind: "unknown", sheet: source.title };
      }
    }
    return unknown;
  };

  /**
   * Что известно о точке: зона по цвету листа и участок по данным геопортала.
   *
   * Две справки независимы и запрашиваются одновременно: зона читается из
   * нашего архива, участок — с чужого сервера через прокси. Ждать вторую,
   * чтобы показать первую, незачем, а любая из них может не ответить.
   */
  const showPointInfo = async (lngLat: { lng: number; lat: number }): Promise<void> => {
    const egg = eggAtPoint(lngLat);
    if (egg) {
      parcelRequest?.abort();
      parcelRequest = null;
      // Звук только по клику. Наведение курсора жестом не считается, браузер
      // всё равно запретил бы воспроизведение — а на движении мыши крик
      // повторялся бы десятки раз в секунду.
      if (egg.sound === "rooster") crow();
      hideHint();
      renderFeatureCard(
        cardNode,
        {
          layerTitle: egg.title,
          sourceLayer: "",
          properties: {},
          geometry: null,
          description: egg.description,
          sourceNote: "пасхалка, а не данные генплана",
        },
        cardCallbacks,
      );
      return;
    }

    parcelRequest?.abort();
    const request = new AbortController();
    parcelRequest = request;

    const [sheetResult, parcelResult] = await Promise.allSettled([
      lookupSheets(lngLat),
      queryParcel(lngLat, request.signal),
    ]);

    // Пока справки летели, пользователь мог кликнуть ещё раз: ответ на
    // предыдущий клик показывать уже нельзя.
    if (parcelRequest !== request) return;
    parcelRequest = null;

    const sheet = sheetResult.status === "fulfilled" ? sheetResult.value : null;
    const zone = sheet?.kind === "zone" ? sheet : null;
    const parcel = parcelResult.status === "fulfilled" ? parcelResult.value : null;

    if (!sheet && !parcel) {
      selectFeature(null);
      return;
    }

    const notes: string[] = [];
    if (zone) notes.push("зона — по цвету листа генплана и его легенде");
    // Для неопознанного обозначения примечания нет: описание карточки уже
    // целиком про это, и повтор той же мыслью выглядел бы как недосмотр.
    if (parcel) {
      notes.push(
        parcel.sourceDate
          ? `участок — по данным геопортала администрации на ${parcel.sourceDate}`
          : "участок — по данным геопортала администрации",
      );
    }

    hideHint();
    renderFeatureCard(
      cardNode,
      {
        layerTitle:
          zone?.zone.title
          ?? (sheet?.kind === "unknown" ? `Обозначение листа «${sheet.sheet}»` : "Земельный участок"),
        sourceLayer: "",
        properties: parcel?.properties ?? {},
        // Ни у зоны, ни у участка геометрии нет: лист растровый, а геопортал
        // отдаёт по точке одни атрибуты. Значит, ни площади по контуру, ни
        // подсветки выбранного объекта не будет — и обещать их нельзя.
        geometry: null,
        description:
          zone?.zone.description
          ?? (sheet?.kind === "unknown"
            ? "Что именно здесь нарисовано, по цвету определить нельзя: обозначения этого "
              + "листа — линии, штриховки и значки. Их расшифровка есть во вкладке «Легенда»."
            : null),
        sourceNote: notes.join("; "),
      },
      cardCallbacks,
    );
  };

  map.on("click", (event) => {
    if (measure.getMode() !== "off") return;

    const target = mapAt(event.point.x);
    const interactive = interactiveLayersOf(target);
    const [hit] = interactive.length
      ? target.queryRenderedFeatures(event.point, { layers: interactive })
      : [];

    if (hit) {
      parcelRequest?.abort();
      parcelRequest = null;
      selectFeature(hit, target);
      return;
    }

    // На сенсорном экране подсказка — единственный быстрый ответ.
    if (hoverless.matches) {
      window.clearTimeout(hintTimer);
      updateHint(
        { x: event.point.x, y: Math.max(0, event.point.y - HINT_TOUCH_LIFT) },
        { lng: event.lngLat.lng, lat: event.lngLat.lat },
      );
      hintTimer = window.setTimeout(() => {
        hintToken += 1;
        hint.hide();
      }, HINT_TOUCH_MS);
    }

    // Своих атрибутов под точкой нет — лист генплана растровый. Зону даёт
    // цвет пикселя, а про земельный участок знает геопортал администрации.
    void showPointInfo(event.lngLat);
  });

  // ---------------------------------------------------------------------
  // Подсказка легенды по наведению
  // ---------------------------------------------------------------------
  const hint = createHoverHint(mapArea);

  /**
   * Зона под курсором ищется не на каждое движение мыши.
   *
   * Событие mousemove приходит десятками раз в секунду, а поиск зоны — это
   * чтение тайла и голосование по окну пикселей. Поэтому запрос ставится в
   * очередь на ближайший кадр и отбрасывается, если курсор с тех пор сдвинулся:
   * показывать подсказку от позиции, которой уже нет, хуже, чем не показывать.
   *
   * Порог в несколько пикселей нужен отдельно: без него дрожание мыши на месте
   * гоняло бы выборку вхолостую.
   */
  const HINT_MIN_MOVE = 6;

  // Устройство без наведения — телефон или планшет. Подсказка по генплану там
  // не появлялась вовсе: её показывает mousemove, которого на сенсорном экране
  // не бывает. Единственным способом узнать зону оставалась карточка, а она
  // ждёт ещё и ответа геопортала, тогда как сама зона читается мгновенно из
  // уже скачанного архива.
  const hoverless = window.matchMedia("(hover: none)");
  /** Сколько держать подсказку после касания: убрать её пальцем нечем. */
  const HINT_TOUCH_MS = 4500;
  /** Подъём над точкой касания: иначе подсказка окажется под пальцем. */
  const HINT_TOUCH_LIFT = 56;

  let hintFrame = 0;
  let hintToken = 0;
  let hintTimer = 0;
  let hintAt: { x: number; y: number } | null = null;

  const updateHint = (point: { x: number; y: number }, lngLat: { lng: number; lat: number }) => {
    const token = (hintToken += 1);

    // Пасхалка отвечает раньше чтения растра: иначе подсказка сначала мигнула
    // бы настоящей зоной, а уже потом сменилась шуткой.
    const egg = eggAtPoint(lngLat);
    if (egg) {
      hint.show(egg.title, firstSentence(egg.description), point.x, point.y);
      return;
    }

    void (async () => {
      const found = await lookupSheets(lngLat);
      if (token !== hintToken) return; // курсор уже в другом месте

      if (found?.kind === "zone") {
        hint.show(found.zone.title, firstSentence(found.zone.description), point.x, point.y);
        return;
      }
      if (found?.kind === "unknown") {
        // Честный ответ вместо молчания: сказать, что обозначение есть и где
        // его прочитать, — полезнее, чем сделать вид, что место пустое.
        hint.show(
          `Обозначение листа «${found.sheet}»`,
          "По цвету не определяется — расшифровка во вкладке «Легенда»",
          point.x,
          point.y,
        );
        return;
      }
      hint.hide();
    })();
  };

  map.on("mousemove", (event) => {
    if (measure.getMode() !== "off") {
      hint.hide();
      return;
    }
    const target = mapAt(event.point.x);
    const interactive = interactiveLayersOf(target);
    const hovering =
      interactive.length > 0 &&
      target.queryRenderedFeatures(event.point, { layers: interactive }).length > 0;
    map.getCanvas().style.cursor = hovering ? "pointer" : "";

    const point = { x: event.point.x, y: event.point.y };
    const lngLat = { lng: event.lngLat.lng, lat: event.lngLat.lat };
    if (
      hintAt &&
      Math.abs(hintAt.x - point.x) < HINT_MIN_MOVE &&
      Math.abs(hintAt.y - point.y) < HINT_MIN_MOVE
    ) {
      return;
    }
    hintAt = point;
    if (hintFrame) return;
    hintFrame = requestAnimationFrame(() => {
      hintFrame = 0;
      updateHint(point, lngLat);
    });
  });

  // Курсор ушёл с карты или карта поехала — подсказка становится враньём о
  // том, что под курсором, поэтому убирается сразу, не дожидаясь новой выборки.
  map.on("mouseout", () => {
    window.clearTimeout(hintTimer);
    hintToken += 1;
    hint.hide();
  });
  map.on("movestart", () => {
    window.clearTimeout(hintTimer);
    hintToken += 1;
    hint.hide();
  });

  map.on("error", (event) => {
    // Ошибки отдельных тайлов не должны обрушивать всю карту: сообщаем и живём
    // дальше, иначе один битый тайл превращается в белый экран.
    console.error("Ошибка MapLibre:", event.error);
  });

  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;
    // В поле поиска Escape закрывает подсказки — не нужно, чтобы одно нажатие
    // делало заодно и второе действие.
    if (event.target instanceof HTMLInputElement) return;
    selectFeature(null);
  });

  /**
   * Отрисовка вкладки «Легенда».
   *
   * Отдельно от renderPanels, потому что вызывается чаще: легенда показывает
   * обозначения только включённых слоёв, а значит обязана меняться при каждом
   * переключении. Перерисовывать заодно и панель слоёв нельзя — сбросились бы
   * прокрутка и фокус ровно в тот момент, когда человек щёлкает чекбоксы.
   */
  /**
   * Переключение темы: оформление, подложка и все слои поверх неё.
   *
   * Вынесено из обработчика панели, потому что вызывается ещё и при смене
   * системной настройки: пока выбор отдан системе, тема в ОС обязана менять и
   * карту, а не только цвет меню.
   */
  const switchTheme = (theme: AppTheme): void => {
    state.theme = theme;
    applyTheme();

    // setStyle сносит все слои, включая тематические, поэтому их приходится
    // добавлять заново после загрузки нового стиля.
    //
    // Предыдущий обработчик снимается: при быстром переключении темы они
    // копятся и повторно добавляют слои поверх уже добавленных.
    if (pendingStyleHandler) map.off("styledata", pendingStyleHandler);
    pendingStyleHandler = () => {
      pendingStyleHandler = null;
      applyThematicLayers();
      // applyThematicLayers расставляет видимость по state.visible, а в режиме
      // сравнения ей распоряжается шторка — возвращаем её правило.
      applyCompareVisibility();
      // Инструмент измерения рисует в собственный GeoJSON-источник, который
      // setStyle тоже снёс.
      measure.restoreLayers();
      if (state.selection) markSelection(state.selection, true);
      // Вторая карта живёт своим стилем и новую тему сама не получит — по
      // разные стороны шторки оказались бы разные подложки.
      if (compareMap) compareMap.setStyle(buildStyle(manifest, fonts, theme));
    };
    map.setStyle(buildStyle(manifest, fonts, theme));
    map.once("styledata", pendingStyleHandler);
    syncPermalink();
  };

  // Пока тема отдана системе, следим за ней: переключение светлой и тёмной в
  // ОС должно менять и оформление, и подложку. При явном выборе не трогаем —
  // человек уже сказал, чего хочет.
  watchSystemTheme(() => {
    if (state.theme === "auto" || state.theme === "none") switchTheme(state.theme);
  });

  function renderLegendPane(): void {
    clear(legendPane);
    legendPane.appendChild(
      createLegend({
        // Только по включённым слоям. Опубликованных листов больше десятка, и
        // показывать обозначения всех разом — значит превратить вкладку в
        // свалку, где обозначения выключенного листа неотличимы от обозначений
        // того, на который человек сейчас смотрит.
        sheetTaxonomies: groups
          .filter((group) => group.raster && state.visible.has(group.key)
            && ZONE_TABLES_BY_SOURCE[group.sourceId])
          .map((group) => ({
            title: group.title,
            zones: ZONE_TABLES_BY_SOURCE[group.sourceId] ?? [],
          })),
        // Картинка легенды показывается всегда, когда она есть, — даже если
        // часть обозначений листа разобрана в таблицу. У сводной карты в
        // таблицу сведён только фон функциональных зон, это двадцать с
        // небольшим строк из примерно девяноста; спрятать картинку значило бы
        // потерять остальные семьдесят.
        sheetLegends: groups
          .filter(
            (group) =>
              group.raster
              && state.visible.has(group.key)
              && manifest.legends?.[group.sourceId],
          )
          .map((group) => ({
            // Когда у листа есть и таблица, и картинка, заголовки разводятся:
            // два раздела с одинаковым названием читаются как повтор.
            title: ZONE_TABLES_BY_SOURCE[group.sourceId]
              ? `${group.title} — остальные обозначения`
              : group.title,
            url: manifest.legends?.[group.sourceId] ?? "",
          })),
      }),
    );
  }

  function renderPanels(): void {
    clear(layersPane);
    layersPane.appendChild(
      createLayerPanel({
        groups,
        visibleKeys: state.visible,
        opacities: state.opacities,
        theme: state.theme,
        onToggleLayer: (key, visible) => {
          if (visible) state.visible.add(key);
          else state.visible.delete(key);
          // В режиме сравнения видимостью распоряжается шторка, а не чекбоксы:
          // применяем общее правило, чтобы состояния не разъезжались.
          applyCompareVisibility();
          // Легенда показывает только включённые слои — значит, после
          // переключения она устарела и должна быть перерисована.
          renderLegendPane();
          syncPermalink();
        },
        onOpacityChange: (key, opacity) => {
          state.opacities.set(key, opacity);
          const group = groups.find((candidate) => candidate.key === key);
          if (group) setGroupOpacity(map, group, opacity);
        },
        onThemeChange: switchTheme,
      }),
    );

    renderLegendPane();

    clear(toolsPane);
    tools = createToolsPanel({
      groups: groups.map((group) => ({ key: group.key, title: group.title })),
      compareEnabled: state.swipe !== null,
      compareLeft: state.compareLeft,
      compareRight: state.compareRight,
      offlineSupported: offlineSupported(),
      offlineStatus: "Проверяю хранилище…",
      onMeasureMode: (mode: MeasureMode) => {
        measure.setMode(mode);
      },
      onMeasureClear: () => {
        measure.clear();
      },
      onCompareToggle: (enabled) => {
        if (enabled) enableCompare();
        else disableCompare();
      },
      onCompareSideChange: (side, groupKey) => {
        if (side === "left") state.compareLeft = groupKey;
        else state.compareRight = groupKey;
        // Правая половина — это отдельная карта со своим набором слоёв,
        // поэтому смена слоя требует её пересоздания.
        if (compareActive()) {
          const position = state.swipe;
          disableCompare();
          state.swipe = position;
          enableCompare();
        }
        syncPermalink();
      },
      onExport: () => {
        void exportView();
      },
      onOfflineDownload: () => {
        void downloadOffline();
      },
      onOfflineRemove: () => {
        void removeOffline();
      },
    });
    toolsPane.appendChild(tools.root);
    void refreshOfflineStatus();
  }
}

/** Ключ слоя для половины шторки: из ссылки, иначе по порядку. */
function pickGroupKey(
  groups: ThematicLayerGroup[],
  fromLink: string | undefined,
  fallbackIndex: number,
): string | null {
  if (fromLink && groups.some((group) => group.key === fromLink)) return fromLink;
  return groups[fallbackIndex]?.key ?? null;
}

/**
 * Какие слои включить при открытии: из ссылки, если она ещё про эти слои.
 *
 * Состав опубликованных листов меняется — лист снимают с публикации, заменяют
 * исправленной версией, переименовывают. Сохранённая ссылка переживает такую
 * замену и приносит ключи, которых больше нет. Брать её содержимое буквально
 * нельзя: получается карта без единого включённого слоя и пустая вкладка
 * легенды, причём без всякого объяснения — человек видит ровно то же, что при
 * поломке приложения.
 *
 * Поэтому из ссылки берутся только существующие ключи, а если не уцелел ни
 * один — состав по умолчанию. Частично устаревшая ссылка уважается: пользователь
 * явно выключил остальные слои, и возвращать их без спроса неправильно.
 */
function restoreVisible(restored: string[] | undefined, groups: ThematicLayerGroup[]): string[] {
  const byDefault = groups.filter((group) => group.defaultVisible).map((group) => group.key);
  if (!restored) return byDefault;

  // Пустой список — это не устаревшая ссылка, а выключенные вручную слои:
  // permalink пишет layers всегда, в том числе пустым, именно чтобы отличить
  // «ничего не включено» от «в ссылке про слои ничего не сказано».
  if (restored.length === 0) return [];

  const known = new Set(groups.map((group) => group.key));
  const surviving = restored.filter((key) => known.has(key));
  return surviving.length > 0 ? surviving : byDefault;
}

function buildStyle(manifest: Manifest, fonts: ReturnType<typeof resolveFonts>, theme: AppTheme) {
  const basemap = manifest.sources[BASEMAP_SOURCE_ID];
  return createBasemapStyle({
    tileUrl: basemap ? absoluteTileUrl(basemap) : "",
    glyphsUrl: GLYPHS_URL,
    attribution: manifest.attribution,
    fonts,
    // Подложки может не быть вовсе — тогда остаётся чистый фон под слоями генплана.
    theme: basemap ? resolveBasemapTheme(theme) : "none",
    minzoom: basemap?.minzoom ?? 0,
    maxzoom: basemap?.maxzoom ?? 14,
    bounds: basemap?.bounds ?? [-180, -85, 180, 85],
  });
}

function initialView(restored: ViewState | undefined, manifest: Manifest): ViewState {
  if (restored) return restored;

  // Приоритет — центр тематического слоя: это и есть территория, на которую
  // разработан генплан.
  //
  // Центр подложки для этого не годится: OSM-экстракт покрывает всю область,
  // и его геометрический центр приходится на Куршский залив. Пользователь,
  // открывший карту генплана города, увидел бы воду.
  const [, thematic] = thematicSources(manifest)[0] ?? [];
  const source = thematic ?? manifest.sources[BASEMAP_SOURCE_ID];
  if (!source || !thematic) return FALLBACK_VIEW;

  const [lng, lat, zoom] = source.center;
  if (!Number.isFinite(lng) || !Number.isFinite(lat) || (lng === 0 && lat === 0)) {
    return FALLBACK_VIEW;
  }

  return {
    lng,
    lat,
    // Центр из заголовка PMTiles указывает на максимальный зум архива — на нём
    // видно один квартал. Отступаем, чтобы в кадр попала вся территория.
    zoom: zoom > 2 ? Math.max(11, zoom - 2) : FALLBACK_VIEW.zoom,
    bearing: 0,
    pitch: 0,
  };
}

function setGroupVisibility(map: MapLibreMap, group: ThematicLayerGroup, visible: boolean): void {
  for (const layerId of group.layerIds) {
    if (map.getLayer(layerId)) {
      map.setLayoutProperty(layerId, "visibility", visible ? "visible" : "none");
    }
  }
}

function setGroupOpacity(map: MapLibreMap, group: ThematicLayerGroup, opacity: number): void {
  if (group.raster) {
    if (map.getLayer(group.fillLayerId)) {
      map.setPaintProperty(group.fillLayerId, "raster-opacity", opacity);
    }
    return;
  }

  const boosted = Math.min(1, opacity + 0.2);
  if (map.getLayer(group.fillLayerId)) {
    map.setPaintProperty(group.fillLayerId, "fill-opacity", opacity);
  }
  if (map.getLayer(group.outlineLayerId)) {
    map.setPaintProperty(group.outlineLayerId, "line-opacity", boosted);
  }
  if (map.getLayer(group.lineLayerId)) {
    map.setPaintProperty(group.lineLayerId, "line-opacity", boosted);
  }
  if (map.getLayer(group.pointLayerId)) {
    map.setPaintProperty(group.pointLayerId, "circle-opacity", boosted);
  }
}

/**
 * Восстанавливает выбранный объект из ссылки.
 *
 * Объект ищется среди загруженных тайлов, а не запрашивается отдельно: вид
 * карты восстанавливается из той же ссылки, значит объект уже в кадре.
 */
function restoreSelection(
  map: MapLibreMap,
  groups: ThematicLayerGroup[],
  encoded: string | null,
  select: (feature: MapGeoJSONFeature | null) => void,
): void {
  if (!encoded) return;

  const separator = encoded.lastIndexOf("__");
  if (separator <= 0) return;
  const rawId = encoded.slice(separator + 2);
  const groupKey = encoded.slice(0, separator);
  const group = groups.find((candidate) => candidate.key === groupKey);
  if (!group) return;

  const featureId: string | number = /^\d+$/.test(rawId) ? Number(rawId) : rawId;

  map.once("idle", () => {
    const layers = group.interactiveLayerIds.filter((layerId) => map.getLayer(layerId));
    if (layers.length === 0) return;
    const match = map
      .queryRenderedFeatures({ layers })
      .find((feature) => feature.id === featureId);
    if (match) select(match);
  });
}

async function copyToClipboard(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    toast("Ссылка скопирована");
  } catch {
    // Clipboard API недоступен без защищённого контекста — честно говорим об этом,
    // а не делаем вид, что ссылка скопирована.
    toast("Не удалось скопировать: скопируйте адрес из строки браузера");
  }
}

function wireSidebar(): void {
  const sidebar = requireElement<HTMLElement>("#sidebar");
  const toggle = document.querySelector<HTMLButtonElement>(".app__sidebar-toggle");
  const tabs = [...document.querySelectorAll<HTMLButtonElement>(".sidebar__tab")];

  toggle?.addEventListener("click", () => {
    const open = sidebar.classList.toggle("sidebar--open");
    toggle.setAttribute("aria-expanded", String(open));
  });

  for (const tab of tabs) {
    tab.addEventListener("click", () => {
      for (const other of tabs) {
        const active = other === tab;
        other.classList.toggle("sidebar__tab--active", active);
        other.setAttribute("aria-selected", String(active));
        const pane = document.getElementById(other.getAttribute("aria-controls") ?? "");
        if (pane) pane.hidden = !active;
      }
    });
  }
}
